import React from 'react';
import { motion } from 'framer-motion';
import ScrollContainer from 'react-indiana-drag-scroll';
import MediaComponent from '../MediaComponent';
import PostStats from '../components/PostStats';
import CommentList from '../components/CommentList';
import PlatformLogo from '../components/PlatformLogo';
import { Creator, Post, VideoData, VideoRefs } from '../types/socialGlass';
import { UI_CONSTANTS } from '../constants/uiConstants';

interface CreatorDetailViewProps {
  selectedCreator: Creator;
  topCreators: Creator[];
  creatorPosts: Post[];
  creatorSentiments: Record<string, string>;
  idToData: Record<string, VideoData>;
  videoRefs: React.MutableRefObject<VideoRefs>;
  handleManualMute: () => void;
  handleManualUnmute: () => void;
  onCreatorSelect: (creator: Creator) => void;
  onBack: () => void;
  isCreatorSelected: (creator: Creator) => boolean;
}

const CreatorDetailView: React.FC<CreatorDetailViewProps> = ({
  selectedCreator,
  topCreators,
  creatorPosts,
  creatorSentiments,
  idToData,
  videoRefs,
  handleManualMute,
  handleManualUnmute,
  onCreatorSelect,
  onBack,
  isCreatorSelected,
}) => {
  return (
    <motion.div
      key="creator-details"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col gap-8 p-6"
      style={{ fontFamily: "Acumin Variable Concept, sans-serif" }}
    >
      {/* Back to summary */}
      <button
        className="mb-2 w-fit text-sm text-[#DB4225] underline hover:opacity-80"
        onClick={onBack}
      >
        Back to summary
      </button>

      {/* Top Creators (again, with highlight, horizontal layout) */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Top Creators</h2>
        </div>
        <ScrollContainer className="hide-scrollbar flex cursor-grab gap-3 overflow-x-auto pb-2 active:cursor-grabbing">
          {topCreators.map((creator, idx) => {
            const selected = isCreatorSelected(creator);
            return (
              <button
                key={idx}
                onClick={() => onCreatorSelect(creator)}
                className={`flex min-w-[276px] max-w-[320px] flex-col gap-0 rounded-xl border-none px-4 py-1 text-left shadow-[0_4px_12px_rgba(0,0,0,0.05),0_-0px_16px_rgba(0,0,0,0.05)] transition-all duration-150 focus:outline-none ${
                  selected ? UI_CONSTANTS.COLORS.PRIMARY_BG : "bg-white"
                }`}
              >
                <div className="flex items-center gap-2">
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    className="h-16 w-16 rounded-full"
                  />
                  <div className="flex flex-1 flex-col overflow-hidden">
                    <div className="truncate text-[16px] font-normal text-black">
                      {creator.name}
                    </div>
                    <div className="truncate text-[14px] text-gray-500">
                      @{creator.name}
                    </div>
                  </div>
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                    <PlatformLogo platform={creator.platform} />
                  </div>
                </div>

                <div className="mx-2 mt-0 flex gap-10 text-[14px] text-gray-600">
                  <div className="flex flex-col">
                    <span>Followers</span>
                    <span className="font-normal text-black">
                      {creator.followers}
                    </span>
                  </div>
                  {creator.platform === "tiktok" && (
                    <div className="flex flex-col">
                      <span>Likes</span>
                      <span className="font-normal text-black">
                        {creator.likes}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mx-2 mt-2 text-[14px] text-[#DB4225]">
                  {creator.mentions} Mentions
                </div>
              </button>
            );
          })}
        </ScrollContainer>
      </div>

      {/* Overall Creator Sentiment */}
      <div>
        <h2 className="mb-2 text-left text-2xl font-bold">
          Overall Creator Sentiment
        </h2>
        <div className="pl-1 text-left text-base text-gray-700">
          {creatorSentiments[selectedCreator.name] || "Missing"}
        </div>
      </div>

      {/* Creator Related Submitted Posts */}
      <div className="w-full">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold">Creator Related Posts</h2>
        </div>
        <ScrollContainer className="hide-scrollbar flex h-full items-start gap-x-8 overflow-x-auto overflow-y-hidden active:cursor-grabbing">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.4 }}
            className="ml-6 flex h-[300px] w-full flex-none overflow-hidden rounded-xl bg-white shadow-lg"
          >
            {creatorPosts.map((post, idx) => (
              <div key={idx} className="flex h-[300px] w-full flex-none overflow-hidden rounded-xl bg-white shadow-lg">
                {/* Left: Video/Image */}
                <div className="flex h-full w-[180px] flex-col items-center justify-center">
                  <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-black shadow">
                    {idToData[post.postId]?.downloaded_path ? (
                      <>
                        <MediaComponent
                          postId={post.postId}
                          username={post.postUsername || post.user.name}
                          index={idx}
                          idToData={idToData}
                          videoRefs={videoRefs}
                          handleManualMute={handleManualMute}
                          handleManualUnmute={handleManualUnmute}
                          muted={true}
                          caption=""
                          showIcon={true}
                          isAutoPlay={false}
                        />
                        <PostStats 
                          likes={post.postLikes} 
                          commentsCount={post.commentsCount} 
                        />
                      </>
                    ) : (
                      <div className="bg-black-200 flex h-full w-full items-center justify-center text-lg font-semibold text-gray-400">
                        Video Not Found
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: User Info, Post Text, Comments */}
                <div className="flex h-full max-w-[480px] flex-col p-2 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {post.postUsername || post.user.name}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-800">
                    {post.postDesc || post.text}
                  </div>
                  <div className="my-2 border-t border-gray-200" />
                  <div className="mb-1 text-sm font-bold">
                    Top Comments
                  </div>
                  <div className="hide-scrollbar flex max-h-40 flex-col gap-1 overflow-y-auto pr-1">
                    {post.topComments && post.topComments.length > 0 ? (
                      <CommentList 
                        comments={post.topComments.map(comment => ({
                          user: { name: comment.username, avatar: "/placeholderpfp.jpg" },
                          text: comment.commentText,
                          likes: comment.likes
                        }))} 
                        showUsernamePrefix={true}
                      />
                    ) : (
                      <div className="text-sm text-gray-400">
                        No comments yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </ScrollContainer>
      </div>
    </motion.div>
  );
};

export default CreatorDetailView;
